/**
 * Map `get_product_details` WebSocket tool_result JSON → HomeProductDetailPage shape
 * ({ product, variants }) matching general_detail_page responses.
 */

function cleanImageUrl(url) {
  if (typeof url !== "string") return "";
  return url
    .trim()
    .replace(/\\"/g, "")
    .replace(/["']+$/g, "")
    .replace(/storage\.cloud\.google\.com/g, "storage.googleapis.com");
}

function normalizeImages(row) {
  /** @type {string[]} */
  const imgs = [];
  if (Array.isArray(row.images)) {
    for (const u of row.images) {
      const c = cleanImageUrl(u);
      if (c) imgs.push(c);
    }
  }
  const uri = cleanImageUrl(row.image_uri || row.uri || row.image || "");
  if (uri && !imgs.includes(uri)) imgs.unshift(uri);
  return imgs;
}

function normalizeAttributes(row) {
  const attrs =
    row.attributes != null &&
    typeof row.attributes === "object" &&
    !Array.isArray(row.attributes)
      ? { ...row.attributes }
      : {};

  if (attrs.color != null) {
    const colorVal = attrs.color;
    if (Array.isArray(colorVal)) {
      const first = colorVal.find((c) => c != null && String(c).trim() !== "");
      attrs.color = first != null ? String(first).trim() : colorVal;
    } else if (typeof colorVal !== "string") {
      attrs.color = String(colorVal);
    }
  }

  if (
    (!attrs.size ||
      (Array.isArray(attrs.size) && attrs.size.length === 0)) &&
    Array.isArray(row.available_sizes) &&
    row.available_sizes.length > 0
  ) {
    attrs.size = row.available_sizes.map(String);
  }

  return attrs;
}

function parsePrice(row) {
  const raw = row.price ?? row.price_info?.price ?? row.unit_price ?? 0;
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? 0));
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 */
export function normalizeCatalogProductRow(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;

  const idRaw = row.id ?? row.product_id ?? row.article_id ?? row.sku;
  if (idRaw == null || String(idRaw).trim() === "") return null;

  const id = String(idRaw).trim();
  const price = parsePrice(row);
  const currency =
    typeof row.currency === "string" && row.currency.trim()
      ? row.currency.trim()
      : typeof row.price_info?.currencyCode === "string"
        ? row.price_info.currencyCode
        : "USD";

  const images = normalizeImages(row);
  const attrs = normalizeAttributes(row);

  const originalPrice =
    typeof row.price_info?.originalPrice === "number"
      ? row.price_info.originalPrice
      : price;

  return {
    ...row,
    id,
    title:
      (typeof row.title === "string" && row.title) ||
      (typeof row.name === "string" && row.name) ||
      "",
    description: typeof row.description === "string" ? row.description : "",
    images,
    image_uri: images[0] ?? "",
    image: images[0] ?? "",
    price,
    currency,
    price_info: {
      ...(row.price_info && typeof row.price_info === "object"
        ? row.price_info
        : {}),
      price,
      originalPrice,
      currencyCode: currency,
    },
    attributes: attrs,
    categories: Array.isArray(row.categories)
      ? row.categories
      : row.category
        ? [String(row.category)]
        : [],
  };
}

/**
 * @param {unknown} raw — parsed tool_result body (object or JSON string)
 * @returns {{ product: object, variants: object[] } | null}
 */
export function toolResultToProductDetail(raw) {
  let row = raw;
  if (typeof raw === "string") {
    try {
      row = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;

  const product = normalizeCatalogProductRow(
    /** @type {Record<string, unknown>} */ (row)
  );
  if (!product) return null;

  const variants = Array.isArray(row.variants)
    ? row.variants
        .map((v) => normalizeCatalogProductRow(v))
        .filter(Boolean)
    : [];

  return { product, variants };
}

/**
 * True when tool_result carries enough data to render HomeProductDetailPage
 * without calling general_detail_page.
 *
 * @param {unknown} detail
 * @param {string | number | null | undefined} expectedProductId
 */
export function isUsableProductDetail(detail, expectedProductId) {
  if (!detail || typeof detail !== "object") return false;
  const d = /** @type {{ product?: { id?: unknown, title?: unknown, images?: unknown[], image_uri?: string, description?: string } }} */ (
    detail
  );
  const product = d.product;
  if (!product?.id) return false;

  if (
    expectedProductId != null &&
    String(product.id) !== String(expectedProductId)
  ) {
    return false;
  }

  const title = product.title != null ? String(product.title).trim() : "";
  if (!title) return false;

  const hasImages =
    (Array.isArray(product.images) && product.images.length > 0) ||
    Boolean(product.image_uri && String(product.image_uri).trim());
  const hasDescription =
    typeof product.description === "string" &&
    product.description.trim().length > 0;

  return hasImages || hasDescription;
}
